import React, { useContext } from 'react';
import { IncentivizedArrowIcon, SearchIcon } from '../../../../assets/icons';
import Select, { Option } from '../../../../components/Select';
import TableHeader from '../../../../components/TableHeader';
import spoolsContext from '../../../../context/spools/spoolsContext';
import { isObject } from '../../../../helpers/validations';
import ASSETS from '../../../../config/constants/assets.json';
import { ALL_ASSETS, ALL_RISK_MODELS } from '../../../../config/constants';
import InfoPopUp from '../../../../components/InfoPopUp';
import appContext from '../../../../context/app/appContext';

type Props = {
    hasFilteredResults: boolean;
};

const ActiveSpoolHeaders: React.FC<Props> = ({ hasFilteredResults }) => {
    const {
        activeFilter,
        searchTerm,
        showSelectDropdown,
        setSearchTerm,
        setActiveFilter,
        toggleFilter,
    } = useContext(spoolsContext);

    const { chainRiskModels } = useContext(appContext);

    const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setSearchTerm(value);
    };

    const handleSelectChange = (option: Option, optionKey: string) => {
        if (!isObject(option)) return;

        Object.entries(option)
            .filter(([key]) => key === optionKey)
            .forEach(([key, value]) => {
                setActiveFilter(key, value);
            });
    };

    const handleOpen = (dataKey: string) => {
        // no state is passed because toggleFilter handles the opening of the dropdown
        toggleFilter(dataKey);
    };

    const handleIncentivizeArrowClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setActiveFilter('incentivized', !activeFilter.incentivized);
    };

    return (
        <>
            <TableHeader withSort={false} dataKey='name' context={spoolsContext}>
                <div className='search-container'>
                    <input
                        type='search'
                        name='searchTerm'
                        placeholder='Name or Address'
                        autoComplete='off'
                        value={searchTerm}
                        onChange={handleSearchTermChange}
                        className={
                            searchTerm && !hasFilteredResults ? 'no-result' : undefined
                        }
                    />
                    <SearchIcon />
                </div>
            </TableHeader>
            <TableHeader dataKey='adjustedApy' context={spoolsContext}>
                <IncentivizedArrowIcon
                    incentivized={activeFilter.incentivized}
                    onClick={handleIncentivizeArrowClick}
                />
                <span>APY</span>
            </TableHeader>
            <TableHeader dataKey='userCurrentBalance' context={spoolsContext}>
                Balance
            </TableHeader>
            <TableHeader
                dataKey='riskModelAddress'
                variant='filter'
                withSort={false}
                context={spoolsContext}
            >
                Risk Model
                <Select
                    selected={
                        <>
                            <span>Risk Model</span>
                            <span className='selected'>
                                {activeFilter.riskModelAddress
                                    ? chainRiskModels[activeFilter?.riskModelAddress]
                                          ?.name ?? ALL_RISK_MODELS
                                    : 'Risk Model'}
                            </span>
                        </>
                    }
                    options={[
                        {
                            riskModelAddress: ALL_RISK_MODELS,
                        },
                        ...Object.keys(chainRiskModels).map((riskModelAddress) => ({
                            riskModelAddress,
                        })),
                    ]}
                    open={showSelectDropdown.riskModelAddress}
                    setOpen={() => handleOpen('riskModelAddress')}
                    optionKey='riskModelAddress'
                    onChange={handleSelectChange}
                    renderOption={(option) =>
                        option.riskModelAddress === ALL_RISK_MODELS
                            ? ALL_RISK_MODELS
                            : chainRiskModels[option.riskModelAddress]?.name ??
                              'Risk Model'
                    }
                />
                {activeFilter.riskModelAddress !== ALL_RISK_MODELS && (
                    <span className='selected-filter'>
                        Filtered : {chainRiskModels[activeFilter.riskModelAddress]?.name}
                    </span>
                )}
            </TableHeader>
            <TableHeader
                dataKey='asset'
                variant='filter'
                withSort={false}
                context={spoolsContext}
            >
                Asset
                <Select
                    selected={
                        <>
                            <span>Asset</span>
                            <span className='selected'>
                                {activeFilter?.asset || 'Asset'}
                            </span>
                        </>
                    }
                    options={[
                        {
                            asset: ALL_ASSETS,
                        },
                        ...Object.values(ASSETS).map((name) => ({
                            asset: name,
                        })),
                    ]}
                    open={showSelectDropdown.asset}
                    setOpen={() => handleOpen('asset')}
                    optionKey='asset'
                    onChange={handleSelectChange}
                    renderOption={(option) => option.asset}
                />
                {activeFilter.asset !== ALL_ASSETS && (
                    <span className='selected-filter'>
                        Filtered : {activeFilter.asset}
                    </span>
                )}
            </TableHeader>
            <TableHeader dataKey='riskTolerance' context={spoolsContext}>
                <div>
                    <span>Risk Appetite</span>
                    <InfoPopUp>
                        Risk Appetite directly correlates to both the risk a Spool takes
                        and the yield it generates in return. The Risk Appetite can be a
                        number between 0 and 10
                    </InfoPopUp>
                </div>
            </TableHeader>
        </>
    );
};

export default ActiveSpoolHeaders;
